import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import { ClinicAuthError } from "@auth/clinicAuthClient";
import { type FixtureBox, toFixture } from "@rooms/roomsClient";

type Wire = components["schemas"];

/**
 * The map is edited and saved whole, so the editor keeps brian's wire shape
 * (`clinic_map/schemas.py` `ClinicMapDocument`) rather than a mapped copy.
 */
export type MapDocument = Wire["ClinicMapDocument"];
export type MapSpace = Wire["Space"];
export type MapDoor = Wire["Door"];
export type MapDevice = Wire["Device"];
export type MapScreen = Wire["Screen"];
export type MapRect = Wire["Rect"];
export type MapSegment = Wire["Segment"];
export type MapArea = Wire["Area"];
export type MapCoverage = Wire["Coverage"];
export type MapOutside = Wire["Outside"];
/** Mirrors brian `map_rules.warnings`; none stops a save. */
export type MapWarning = Wire["MapWarning"];
/** Mirrors brian `clinic_map/schemas.py` `ClinicMapLive` (BRI-269). */
export type ClinicMapLive = Wire["ClinicMapLive"];
export type LiveOccupant = Wire["LiveOccupant"];
/** Mirrors brian `clinic_map/schemas.py` `MapDisplay`: a display and whether its screen is online. */
export type MapDisplay = Wire["MapDisplay"];
export type DisplayStatus = Wire["DisplayStatus"];
export type MapDisplays = ReadonlyMap<string, MapDisplay>;

export const displaysById = (displays: readonly MapDisplay[]): MapDisplays =>
  new Map(displays.map((d) => [d.display_uuid, d]));

export interface ClinicMap {
  /** 0 with an empty document: nothing saved yet. */
  revision: number;
  document: MapDocument;
  warnings: MapWarning[];
  /** Every display, placed or not. */
  displays: MapDisplay[];
}

export const AREA_LABEL: Record<MapArea, string> = {
  hallway: "Hallway",
  washroom: "Washroom",
  other: "Other area",
};

export const OUTSIDE_LABEL: Record<MapOutside, string> = {
  client_entrance: "Client entrance",
  staff_entrance: "Staff entrance",
  other: "Other way out",
};

export const OUTSIDE_KINDS = Object.keys(OUTSIDE_LABEL) as MapOutside[];

export const DISPLAY_STATUS_LABEL: Record<DisplayStatus, string> = {
  online: "Online",
  offline: "Offline",
  not_paired: "Not paired",
};

export const COVERAGE_LABEL: Record<MapCoverage, string> = {
  full: "Sees all of it",
  blind_spots: "Has blind spots",
};

/** An edit to the map being drawn; a function edits whatever the draft is by then. */
export type DocEdit = (
  next: MapDocument | ((current: MapDocument) => MapDocument),
) => void;

/** Mirrors brian `clinic_map/schemas.py` `MAX_BOXES_PER_SPACE`. */
export const MAX_BOXES_PER_SPACE = 8;
/** Mirrors brian `room/schemas.py` `MAX_DOOR_AREAS`. */
export const MAX_DOOR_AREAS = 16;

/** Mirrors brian `room/schemas.py` `DoorArea`. */
interface DoorArea {
  doorUuid: string;
  box: FixtureBox;
}

/** Mirrors brian `RoomDoorAreasOut`: in the pixels of a `width` by `height` picture. */
interface RoomDoorAreas {
  active: boolean;
  width: number;
  height: number;
  doors: DoorArea[];
}

/** Mirrors brian `room/schemas.py` `RoomDoorAreasSet`. */
export interface DoorAreasChange {
  /** On the room's empty-room baseline, or on the still `stillReference` names. */
  drawnOn: "baseline" | "still";
  stillReference: string | null;
  doors: DoorArea[];
}

const toDoorAreas = (a: Wire["RoomDoorAreasOut"]): RoomDoorAreas => ({
  active: a.active,
  width: a.width,
  height: a.height,
  doors: a.doors.map((d) => ({ doorUuid: d.door_uuid, box: toFixture(d.box) })),
});

export const clinicMapClient = {
  get: (signal?: AbortSignal): Promise<ClinicMap> =>
    request(() => apiClient.GET("/clinic-map", { signal })),

  live: (signal?: AbortSignal): Promise<ClinicMapLive> =>
    request(() => apiClient.GET("/clinic-map/live", { signal })),

  /** Leadership only. 409 `map_changed` when someone saved since `expectedRevision`; 422 names what stops the save. */
  save: (expectedRevision: number, document: MapDocument): Promise<ClinicMap> =>
    request(() =>
      apiClient.PUT("/clinic-map", {
        body: { expected_revision: expectedRevision, document },
      }),
    ),

  /** Leadership only. Null when no doors are drawn on the room's camera (404). */
  doorAreas: (roomUuid: string): Promise<RoomDoorAreas | null> =>
    request(() =>
      apiClient.GET("/rooms/{room_uuid}/camera/doors", {
        params: { path: { room_uuid: roomUuid } },
      }),
    ).then(toDoorAreas, (error: unknown) => {
      if (error instanceof ClinicAuthError && error.status === 404) return null;
      throw error;
    }),

  /** Leadership only. Each door must be on the room's walls on the saved map. */
  setDoorAreas: (roomUuid: string, change: DoorAreasChange) =>
    request(() =>
      apiClient.PUT("/rooms/{room_uuid}/camera/doors", {
        params: { path: { room_uuid: roomUuid } },
        body: {
          drawn_on: change.drawnOn,
          still_reference: change.stillReference,
          doors: change.doors.map(({ doorUuid, box }) => ({
            door_uuid: doorUuid,
            box: [...box],
          })),
        },
      }),
    ).then(toDoorAreas),
};
